import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';

// Material dictionary for analytics: density [g/cm³] turns wound metres into kilograms.
// Aliases map spelling variants found on production sheets ("PLA MAGIC SILK",
// "MAGIC PLA SILK") onto one canonical material, so every KPI sums the same thing.

interface Material {
  id: number;
  name: string;
  density_g_cm3: number | null;
  grams_per_meter_175: number | null;
  grams_per_meter_285: number | null;
  notes: string | null;
  is_active: boolean;
  aliases: string[];
  usage_count: number;
}

interface Unmatched {
  material: string;
  count: number;
}

interface FormState {
  name: string;
  density_g_cm3: string;
  notes: string;
  is_active: boolean;
  aliases: string;
}

const EMPTY: FormState = { name: '', density_g_cm3: '', notes: '', is_active: true, aliases: '' };

type Filter = 'all' | 'missing' | 'used';

const Materials: React.FC = () => {
  const { t } = useLanguage();
  const [materials, setMaterials] = useState<Material[]>([]);
  const [unmatched, setUnmatched] = useState<Unmatched[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [form, setForm] = useState<FormState>(EMPTY);
  const [editing, setEditing] = useState<Material | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    try {
      const [m, u] = await Promise.all([api.get('/materials'), api.get('/materials/unmatched')]);
      setMaterials(m.data.materials);
      setUnmatched(u.data.unmatched);
    } catch {
      setMaterials([]);
      setUnmatched([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openNew = (preset?: Partial<FormState>) => {
    setEditing(null);
    setForm({ ...EMPTY, ...preset });
    setError(null);
    setShowForm(true);
  };
  const openEdit = (m: Material, extraAlias?: string) => {
    setEditing(m);
    const aliases = extraAlias ? [...m.aliases, extraAlias] : m.aliases;
    setForm({
      name: m.name,
      density_g_cm3: m.density_g_cm3 !== null ? String(m.density_g_cm3) : '',
      notes: m.notes ?? '',
      is_active: m.is_active,
      aliases: aliases.join('\n'),
    });
    setError(null);
    setShowForm(true);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    const density = form.density_g_cm3.trim().replace(',', '.');
    const payload = {
      name: form.name.trim(),
      density_g_cm3: density ? parseFloat(density) : null,
      notes: form.notes.trim() || null,
      is_active: form.is_active,
      aliases: form.aliases.split(/[\n,;]/).map(a => a.trim()).filter(Boolean),
    };
    try {
      if (editing) await api.put(`/materials/${editing.id}`, payload);
      else await api.post('/materials', payload);
      setShowForm(false);
      fetchAll();
    } catch (err: any) {
      const errors = err?.response?.data?.errors;
      setError(errors ? Object.values(errors).flat().join(' ') : (err?.response?.data?.message ?? t('materials.saveError')));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (m: Material) => {
    if (!window.confirm(t('materials.confirmDelete', { name: m.name }))) return;
    try {
      await api.delete(`/materials/${m.id}`);
      fetchAll();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? t('materials.saveError'));
    }
  };

  // Attach an unmatched sheet name as an alias: pick the target material from a prompt-less
  // flow — the row's "assign" button opens the edit form of the material chosen in a select.
  const [assignTarget, setAssignTarget] = useState<Record<string, number>>({});
  // Mostly junk from the import ("1", "4.5"), so the list starts collapsed.
  const [showUnmatched, setShowUnmatched] = useState(false);
  const assignAlias = (u: Unmatched) => {
    const id = assignTarget[u.material];
    const target = materials.find(m => m.id === id);
    if (!target) return;
    openEdit(target, u.material);
  };

  const visible = useMemo(() => {
    const q = search.trim().toUpperCase();
    return materials.filter(m => {
      if (filter === 'missing' && m.density_g_cm3 !== null) return false;
      if (filter === 'used' && m.usage_count === 0) return false;
      if (!q) return true;
      return m.name.toUpperCase().includes(q) || m.aliases.some(a => a.toUpperCase().includes(q));
    });
  }, [materials, search, filter]);

  const missingCount = materials.filter(m => m.density_g_cm3 === null).length;
  const fmt = (v: number | null, digits: number) => (v === null ? '—' : v.toFixed(digits));

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1e293b' }}>🧪 {t('materials.title')}</h1>
          <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: 14, maxWidth: 820 }}>{t('materials.subtitle')}</p>
        </div>
        <button onClick={() => openNew()} style={btnPrimary}>+ {t('materials.add')}</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
        <Stat label={t('materials.statTotal')} value={String(materials.length)} />
        <Stat label={t('materials.statMissing')} value={String(missingCount)} tone={missingCount > 0 ? 'warn' : 'ok'} />
        <Stat label={t('materials.statUnmatched')} value={String(unmatched.length)} tone={unmatched.length > 0 ? 'warn' : 'ok'} />
      </div>

      {error && !showForm && (
        <div style={alertStyle}>{error}</div>
      )}

      {unmatched.length > 0 && (
        <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10, padding: '12px 16px', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ fontWeight: 700, color: '#92400e' }}>{t('materials.unmatchedTitle')} ({unmatched.length})</div>
            <button onClick={() => setShowUnmatched(v => !v)} style={btnSmall}>{showUnmatched ? t('materials.hide') : t('materials.show')}</button>
          </div>
          {showUnmatched && <div style={{ fontSize: 13, color: '#78350f', margin: '6px 0 10px' }}>{t('materials.unmatchedHint')}</div>}
          {showUnmatched && <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {unmatched.map(u => (
              <div key={u.material} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 13 }}>
                <span style={{ fontWeight: 700, minWidth: 160 }}>{u.material}</span>
                <span style={{ color: '#92400e' }}>{t('materials.unmatchedCount', { count: String(u.count) })}</span>
                <select
                  value={assignTarget[u.material] ?? ''}
                  onChange={e => setAssignTarget({ ...assignTarget, [u.material]: Number(e.target.value) })}
                  style={{ ...inp, padding: '5px 8px', fontSize: 12 }}
                >
                  <option value="">{t('materials.pickMaterial')}</option>
                  {materials.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                <button onClick={() => assignAlias(u)} disabled={!assignTarget[u.material]} style={btnSmall}>{t('materials.assignAlias')}</button>
                <button onClick={() => openNew({ name: u.material })} style={btnSmall}>{t('materials.createFrom')}</button>
              </div>
            ))}
          </div>}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('materials.searchPlaceholder')} style={{ ...inp, minWidth: 260 }} />
        {(['all', 'missing', 'used'] as Filter[]).map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{ ...btnSmall, background: filter === f ? '#667eea' : '#fff', color: filter === f ? '#fff' : '#475569', borderColor: filter === f ? '#667eea' : '#cbd5e1' }}>
            {t(`materials.filter_${f}`)}
          </button>
        ))}
        <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 'auto' }}>{t('materials.shown', { count: String(visible.length) })}</span>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              <th style={th}>{t('materials.colName')}</th>
              <th style={th}>{t('materials.colDensity')}</th>
              <th style={th}>g/m Ø1,75</th>
              <th style={th}>g/m Ø2,85</th>
              <th style={th}>{t('materials.colAliases')}</th>
              <th style={th}>{t('materials.colUsage')}</th>
              <th style={th}>{t('materials.colActive')}</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: 32 }}>{t('common.loading')}</td></tr>
            ) : visible.length === 0 ? (
              <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: 32 }}>{t('materials.empty')}</td></tr>
            ) : visible.map(m => (
              <tr key={m.id} style={{ opacity: m.is_active ? 1 : 0.5 }}>
                <td style={{ ...td, fontWeight: 700 }}>
                  {m.name}
                  {m.notes && <div style={{ fontSize: 11, fontWeight: 400, color: '#94a3b8' }}>{m.notes}</div>}
                </td>
                <td style={{ ...td, fontWeight: 700, color: m.density_g_cm3 === null ? '#d97706' : '#1e293b' }}>
                  {m.density_g_cm3 === null ? t('materials.noDensity') : `${m.density_g_cm3.toFixed(3)} g/cm³`}
                </td>
                <td style={td}>{fmt(m.grams_per_meter_175, 3)}</td>
                <td style={td}>{fmt(m.grams_per_meter_285, 3)}</td>
                <td style={{ ...td, color: '#64748b' }}>{m.aliases.length ? m.aliases.join(', ') : '—'}</td>
                <td style={td}>{m.usage_count}</td>
                <td style={td}>{m.is_active ? '✔' : '—'}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  <button onClick={() => openEdit(m)} style={btnSmall}>✏️ {t('common.edit')}</button>
                  <button onClick={() => remove(m)} style={{ ...btnSmall, color: '#dc3545', marginLeft: 6 }}>🗑</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, maxWidth: 520, width: '100%', boxShadow: '0 12px 40px rgba(0,0,0,0.25)' }}>
            <h2 style={{ margin: '0 0 16px 0', fontSize: 20, color: '#1e293b' }}>{editing ? t('materials.editTitle') : t('materials.addTitle')}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <label style={{ ...lbl, gridColumn: '1 / -1' }}>{t('materials.colName')}
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="PLA TOUGH FF" style={inp} autoFocus />
                <span style={hint}>{t('materials.nameHint')}</span>
              </label>
              <label style={lbl}>{t('materials.colDensity')} [g/cm³]
                <input value={form.density_g_cm3} onChange={e => setForm({ ...form, density_g_cm3: e.target.value })} placeholder="1.24" style={inp} />
                <span style={hint}>{t('materials.densityHint')}</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#475569', alignSelf: 'end', paddingBottom: 22 }}>
                <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} /> {t('materials.colActive')}
              </label>
              <label style={{ ...lbl, gridColumn: '1 / -1' }}>{t('materials.colAliases')}
                <textarea value={form.aliases} onChange={e => setForm({ ...form, aliases: e.target.value })} rows={3} placeholder={'PLA MAGIC SILK\nMAGIC PLA SILK'} style={{ ...inp, fontFamily: 'inherit', resize: 'vertical' }} />
                <span style={hint}>{t('materials.aliasesHint')}</span>
              </label>
              <label style={{ ...lbl, gridColumn: '1 / -1' }}>{t('materials.colNotes')}
                <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} style={inp} />
              </label>
            </div>
            {error && <div style={{ ...alertStyle, margin: '12px 0 0' }}>{error}</div>}
            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
              <button onClick={save} disabled={saving} style={{ ...btnPrimary, flex: 1, padding: '12px 0', fontSize: 15, opacity: saving ? 0.6 : 1 }}>
                {saving ? '...' : t('common.save')}
              </button>
              <button onClick={() => setShowForm(false)} disabled={saving} style={{ flex: 1, padding: '12px 0', background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string; tone?: 'ok' | 'warn' }> = ({ label, value, tone }) => (
  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px' }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: 0.5, textTransform: 'uppercase' }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 800, color: tone === 'warn' ? '#d97706' : tone === 'ok' ? '#059669' : '#1e293b' }}>{value}</div>
  </div>
);

const th: React.CSSProperties = { padding: '10px 12px', textAlign: 'left', fontSize: 11, color: '#6c757d', borderBottom: '2px solid #dee2e6', whiteSpace: 'nowrap', letterSpacing: 0.5 };
const td: React.CSSProperties = { padding: '10px 12px', borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle' };
const lbl: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: '#475569' };
const inp: React.CSSProperties = { padding: '9px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14 };
const hint: React.CSSProperties = { fontSize: 11, fontWeight: 400, color: '#94a3b8' };
const btnSmall: React.CSSProperties = { padding: '5px 10px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#475569', cursor: 'pointer' };
const btnPrimary: React.CSSProperties = { padding: '10px 18px', background: '#667eea', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' };
const alertStyle: React.CSSProperties = { background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#991b1b' };

export default Materials;
